import React from 'react';
import {
  PlanContainer,
  SubtitleSection,
  SubtitleIcon,
  CurrentPlanInfo,
  RolloverSection,
  RolloverIcon,
  RolloverText,
  RolloverPages,
  SectionTitle,
  PlanCard,
  PlanHeader,
  PlanContent,
  InfoSection,
  InfoText
} from './styles';
import {
  IconInfo,
  IconPaperCollection,
  IconPrinterInkDrop
} from '@veneer/core';
import PrinterDetails from './PrinterDetails';
import PlanCostDetails from './PlanCostDetails';
import { primitives } from '@veneer/primitives';
import { useData } from 'src/contexts/DataContext';
import { PrinterItem } from 'src/utils/Products/productFilter';
import { useTranslationWithInterpolation } from 'src/hooks/useTranslationWithValues/useTranslationWithValuesHelper';

interface PlanSummaryProps {
  selectedPrinterDetails?: PrinterItem;
  currentPrinterDetails?: PrinterItem[];
}

const PlanSummary = ({
  selectedPrinterDetails,
  currentPrinterDetails
}: PlanSummaryProps): JSX.Element => {
  const { rolloverData } = useData();
  const { getText, getTextWithValues } = useTranslationWithInterpolation();
  const color = primitives.color;

  const totalPagesApplicable = rolloverData?.totalPagesApplicable || 0;
  const totalPagesPrinted = rolloverData?.totalPagesPrinted || 0;
  const remainingPages = totalPagesApplicable - totalPagesPrinted;
  const planNote = getTextWithValues(
    'planSummary.currentPlan',
    'Your current plan is {{printerModel}} printer with {{pages}} pages/month.',
    {
      printerModel: currentPrinterDetails[0]?.printerName,
      pages: currentPrinterDetails[0]?.inkPlan[0]?.pagesPerMonth
    }
  );

  const recommendedPlan = selectedPrinterDetails?.inkPlan.find(
    (plan) => plan.recommendedPlan === true
  );

  return (
    <PlanContainer>
      <SubtitleSection>
        <SubtitleIcon>
          <IconPrinterInkDrop size={20} />
        </SubtitleIcon>
        <div>
          <CurrentPlanInfo>{planNote}</CurrentPlanInfo>
          <CurrentPlanInfo>
            <b>
              {getTextWithValues(
                'planSummary.newPlanInfo',
                'Your new plan will be {{printerModel}} printer with {{pages}} pages/month',
                {
                  printerModel: selectedPrinterDetails?.printerName,
                  pages: recommendedPlan?.pagesPerMonth
                }
              )}
            </b>
          </CurrentPlanInfo>
        </div>
      </SubtitleSection>
      {remainingPages > 0 && (
        <RolloverSection>
          <RolloverIcon>
            <IconPaperCollection size={32}></IconPaperCollection>
          </RolloverIcon>
          <div>
            <RolloverText>
              {getText(
                'planSummary.rolloverText',
                'Your rollover pages will be transferred automatically to your new plan, up to a cap of XXX pages'
              )}
            </RolloverText>
            <RolloverPages>
              {getTextWithValues(
                'planSummary.rolloverPages',
                '{{remainingPages}} left / {{totalPagesApplicable}}',
                {
                  remainingPages,
                  totalPagesApplicable
                }
              )}
            </RolloverPages>
          </div>
        </RolloverSection>
      )}
      <PlanCard>
        <PlanHeader>{/* Card Header */}</PlanHeader>
        <PlanContent>
          <SectionTitle>
            {getText(
              'planSummary.selectedPlanTitle',
              'Your selected HP All-In Plan'
            )}
          </SectionTitle>
          <PrinterDetails selectedPrinterDetails={selectedPrinterDetails} />
          <InfoSection>
            <IconInfo
              filled
              color={color.electricBlue6}
              height={32}
            />
            <InfoText>
              {getText(
                'planSummary.adjustPlanInfo',
                'You can adjust your upgrade print plan once the upgrade begins.'
              )}
            </InfoText>
          </InfoSection>
          <PlanCostDetails selectedPrinterDetails={selectedPrinterDetails} />
        </PlanContent>
      </PlanCard>
    </PlanContainer>
  );
};

export default PlanSummary;
